import React from 'react';
import { useNavigate } from 'react-router-dom';
import { faGamepad, faCircleCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import AccessStatusCard from '../access/AccessStatusCard.jsx';
import { useApprovedGameAccessRefresh } from '../../authentication/useApprovedGameAccessRefresh.js';
import './NoGameAccessPlaceholder.css';

/**
 * Placeholder shown by <RequireGameAccess> when the user is a member of the
 * currently-selected org but has not been granted access to any game in it.
 *
 * Distinct from the "no game selected" state pages render locally — that one
 * means "pick a game from the switcher"; this one means "you don't have any
 * game to pick yet". Its action goes to the Games page, which lists every game
 * in the org with a Request access button on the ones the user lacks — the
 * request itself needs a specific game, so it is made there rather than here.
 *
 * If a game-access request the user submitted has just been APPROVED, we instead
 * surface the shared "Access granted" card (the same one the org-join waiting
 * screen and the My-Access-Requests subtab use) so the user knows to refresh —
 * no re-login — rather than only finding out via the bell.
 */
const NoGameAccessPlaceholder = ({
    title = 'No game access yet',
    subtitle = "You don't have access to a game in this section yet. The Games page lists every game in this organization: choose “Request access” on the one you need, or wait for your organization or game owner to invite you."
}) => {
    const navigate = useNavigate();
    const { newlyApproved, entering, onEnter } = useApprovedGameAccessRefresh();

    if (newlyApproved.length > 0) {
        return (
            <div className="no-game-access">
                <AccessStatusCard
                    tone="success"
                    icon={faCircleCheck}
                    title="Access granted"
                    message={newlyApproved.length === 1
                        ? 'Your game access request was approved. Refresh to start using it — no need to sign in again.'
                        : `${newlyApproved.length} of your game access requests were approved. `
                          + 'Refresh to start using them — no need to sign in again.'}
                    action={{
                        label: 'Refresh access & continue',
                        busyLabel: 'Refreshing…',
                        busy: entering,
                        onClick: onEnter,
                        icon: faCircleCheck,
                    }}
                />
            </div>
        );
    }

    return (
        <div className="no-game-access">
            <AccessStatusCard
                tone="neutral"
                icon={faGamepad}
                title={title}
                message={subtitle}
                action={{
                    label: 'Browse games',
                    icon: faArrowRight,
                    variant: 'secondary',
                    onClick: () => navigate('/game'),
                }}
            />
        </div>
    );
};

export default NoGameAccessPlaceholder;
